import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import ContactForm from "@/pages/contact/components/ContactForm";
import { siteInfo } from "@/mocks/site";
import { useSEO, SITE_ORIGIN } from "@/hooks/useSEO";

const channels = [
  {
    icon: "ri-mail-line",
    label: "内容纠错",
    value: siteInfo.email,
    href: `mailto:${siteInfo.email}`,
    desc: "发现词条信息有误，附上公开来源链接，我们会核实后修改。",
  },
  {
    icon: "ri-briefcase-line",
    label: "合作与其他",
    value: siteInfo.businessEmail,
    href: `mailto:${siteInfo.businessEmail}`,
    desc: "内容合作、数据引用或授权相关事宜，请通过这个邮箱联系。",
  },
  {
    icon: "ri-time-line",
    label: "回复时间",
    value: siteInfo.replyTime,
    href: "",
    desc: "节假日提交的留言会在节后第一个工作日集中处理。",
  },
];

export default function ContactPage() {
  useSEO({
    title: "联系我们 - 黑料百科纠错与话题推荐",
    description:
      "联系黑料百科：提交话题推荐、内容纠错或合作洽谈。填写在线留言表单即可，我们在工作日 24 小时内回复，并附上公开来源以便核实。",
    keywords: "联系黑料百科,内容纠错,话题推荐,合作洽谈,留言反馈",
    path: "/contact",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系我们 - 黑料百科",
        url: `${SITE_ORIGIN}/contact`,
        inLanguage: "zh-CN",
        description: "提交话题推荐、内容纠错或合作洽谈的联系页面。",
        mainEntity: {
          "@type": "Organization",
          name: "黑料百科",
          url: SITE_ORIGIN,
          email: siteInfo.email,
          contactPoint: {
            "@type": "ContactPoint",
            contactType: "customer support",
            email: siteInfo.email,
            availableLanguage: ["zh-CN"],
          },
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "联系我们", item: `${SITE_ORIGIN}/contact` },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100/60 px-4 py-4 md:px-6">
        <Breadcrumb items={[{ label: "联系我们" }]} />
      </div>

      <section className="border-b border-background-200 bg-background-50 px-4 py-10 md:px-6 md:py-14">
        <span className="flex h-12 w-12 items-center justify-center rounded-md bg-accent-100 text-accent-700">
          <i className="ri-customer-service-2-line text-2xl"></i>
        </span>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">联系我们</h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
          无论是想推荐一个话题，还是发现词条内容需要更正，都可以直接留言。附上公开来源链接，处理会更快。
        </p>
      </section>

      <div className="grid grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-12 lg:grid-cols-[minmax(0,1fr)_320px]">
        <ContactForm />

        <aside className="flex flex-col gap-6">
          {channels.map((item) => (
            <div key={item.label} className="rounded-lg border border-background-200 bg-background-100/60 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-50 text-primary-700">
                <i className={`${item.icon} text-lg`}></i>
              </span>
              <h2 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.label}</h2>
              {item.href ? (
                <a href={item.href} className="mt-1 block text-sm text-primary-700 transition-colors hover:text-primary-800">
                  {item.value}
                </a>
              ) : (
                <p className="mt-1 text-sm text-foreground-800">{item.value}</p>
              )}
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">{item.desc}</p>
            </div>
          ))}
        </aside>
      </div>
    </SiteLayout>
  );
}